/**
 * Global Options and View Utility
 * Provides centralized access to selected product options and view stored on window object
 */

/**
 * Get the currently selected options from global storage
 * @returns {Object} The current selected options
 */
export const getGlobalSelectedOptions = () => {
  return window.selectedOptions || {};
};

/**
 * Set the selected options globally
 * @param {Object} options - The options to store globally
 */
export const setGlobalSelectedOptions = (options) => {
  window.selectedOptions = options;
  console.log('🌍 Global options updated:', window.selectedOptions);
  try {
    const evt = new CustomEvent('globalOptionsChanged', { detail: { selectedOptions: window.selectedOptions } });
    window.dispatchEvent(evt);
  } catch (e) {
    // no-op
  }
};

/**
 * Get a specific option value by key
 * @param {string} optionKey - The option key (e.g., 'Option1', 'Option2')
 * @returns {string|null} The option value or null if not found
 */
export const getGlobalOptionValue = (optionKey) => {
  const options = getGlobalSelectedOptions();
  if (!options || !options[optionKey]) {
    return null;
  }
  
  // Extract value if it's in {value: 'something'} format
  const option = options[optionKey];
  return option.value || option;
};

/**
 * Check if global options are available
 * @returns {boolean} True if global options exist
 */
export const hasGlobalOptions = () => {
  return window.selectedOptions && Object.keys(window.selectedOptions).length > 0;
};

/**
 * Clear global options
 */
export const clearGlobalOptions = () => {
  window.selectedOptions = {};
  console.log('🧹 Global options cleared');
};

/**
 * Get formatted options for URL construction
 * @returns {Object} Options formatted for URL paths
 */
export const getGlobalOptionsForUrl = () => {
  const options = getGlobalSelectedOptions();
  const result = {};
  
  Object.keys(options).forEach(key => {
    const value = getGlobalOptionValue(key);
    if (value) {
      result[key] = value;
    }
  });
  
  return result;
};

// ===== GLOBAL VIEW MANAGEMENT =====

/**
 * Get the currently selected view from global storage
 * @returns {Object|string|null} The current selected view
 */
export const getGlobalSelectedView = () => {
  return window.selectedView || null;
};

/**
 * Set the selected view globally
 * @param {Object|string} view - The view to store globally
 */
export const setGlobalSelectedView = (view) => {
  window.selectedView = view;
  console.log('🎯 Global view updated:', window.selectedView);
};

/**
 * Get view ID from global view (handles both string and object formats)
 * @returns {string} The view ID
 */
export const getGlobalViewId = () => {
  const view = getGlobalSelectedView();
  if (!view) return '';
  if (typeof view === 'string') return view;
  if (typeof view === 'object' && view.id) return view.id;
  return String(view);
};

/**
 * Check if global view is available
 * @returns {boolean} True if global view exists
 */
export const hasGlobalView = () => {
  return window.selectedView !== null && window.selectedView !== undefined;
};

/**
 * Clear global view
 */
export const clearGlobalView = () => {
  window.selectedView = null;
  console.log('🧹 Global view cleared');
};

/**
 * Clear both global options and view
 */
export const clearGlobalState = () => {
  clearGlobalOptions();
  clearGlobalView();
  console.log('🧹 All global state cleared');
};
